import { useState } from "react";
import { Users, UserPlus, Pencil, RefreshCw, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, PageHeader, Pager, Panel, RowsSkeleton, SearchBox, StatusBadge } from "@/components/app/kit";
import { FormDialog } from "@/components/app/FormDialog";
import { InviteNotice } from "@/pages/platform/TenantNew";
import { useFetch, useServerList } from "@/hooks/useServerList";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function TenantUsers() {
  const { can } = useAuth();
  const manage = can("users:manage");
  const list = useServerList("/t/users");
  const roles = useFetch("/t/roles");
  const [invite, setInvite] = useState(false);
  const [edit, setEdit] = useState(null);
  const [link, setLink] = useState(null);
  const roleOptions = (roles.data || []).map((r) => [r.id, r.name]);
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Administration" title="Utilisateurs du cabinet" subtitle="Collaborateurs, invitations et rôles attribués."
        actions={manage && <Button onClick={() => setInvite(true)} data-testid="users-invite-button"><UserPlus className="h-4 w-4 mr-2" />Inviter un collaborateur</Button>} />
      {link && <div className="mb-6"><InviteNotice url={link.url} email={link.email} /></div>}
      <Panel className="overflow-hidden">
        <div className="-mt-1 mb-4"><SearchBox value={list.filters.search} onChange={(v) => list.setFilter("search", v)} placeholder="Nom ou e-mail…" testid="users-search" /></div>
        <div className="-mx-5 overflow-x-auto">
          {list.loading ? <RowsSkeleton /> : list.items.length === 0 ? <EmptyState icon={Users} title="Aucun utilisateur" /> : (
            <Table>
              <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>E-mail</TableHead><TableHead>Rôle</TableHead><TableHead>Statut</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {list.items.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                    <TableCell className="font-medium">{u.name}{u.is_me && <span className="text-xs text-slate-400"> (vous)</span>}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell className="text-sm">{u.role_name}</TableCell>
                    <TableCell><StatusBadge status={u.status} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {manage && !u.is_me && (
                        <>
                          {u.status === "invited" && (
                            <Button variant="ghost" size="sm" title="Régénérer l'invitation" data-testid={`user-reinvite-${u.email}`}
                              onClick={() => act(async () => { const { data } = await api.post(`/t/users/${u.id}/invitation`); setLink({ url: data.invite_url, email: u.email }); }, "Nouveau lien généré")}>
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          )}
                          {u.status === "active" && (
                            <Button variant="ghost" size="sm" title="Fermer ses sessions" data-testid={`user-revoke-${u.email}`}
                              onClick={() => act(() => api.post(`/t/users/${u.id}/revoke-sessions`), "Sessions fermées")}>
                              <LogOut className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => setEdit(u)} data-testid={`user-edit-${u.email}`}><Pencil className="h-4 w-4" /></Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <div className="-mx-5 -mb-5"><Pager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} /></div>
      </Panel>
      <FormDialog open={invite} onOpenChange={setInvite} testid="user-invite-dialog" title="Inviter un collaborateur" submitLabel="Générer l'invitation"
        description="Le collaborateur définira lui-même son mot de passe via un lien sécurisé valable 7 jours." initial={{}}
        fields={[{ key: "name", label: "Nom complet", required: true }, { key: "email", label: "E-mail", type: "email", required: true },
          { key: "role_id", label: "Rôle", type: "select", options: roleOptions, required: true, full: true }]}
        onSubmit={async (v) => {
          const { data } = await api.post("/t/users/invite", v);
          if (data.invite_url) setLink({ url: data.invite_url, email: v.email });
          toast.success(data.already_active ? "Utilisateur existant rattaché au cabinet" : "Invitation générée");
          list.reload();
        }} />
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} testid="user-edit-dialog" title={`Modifier ${edit?.name}`}
        description="La désactivation ferme immédiatement ses sessions dans ce cabinet."
        initial={{ role_id: edit?.role_id, status: edit?.status === "disabled" ? "disabled" : "active" }}
        fields={[{ key: "role_id", label: "Rôle", type: "select", options: roleOptions, required: true },
          { key: "status", label: "Statut", type: "select", options: [["active", "Actif"], ["disabled", "Désactivé"]], required: true }]}
        onSubmit={async (v) => { await api.put(`/t/users/${edit.id}`, v); toast.success("Utilisateur mis à jour"); list.reload(); }} />
    </div>
  );
}
